import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';

import { UseOutletContextConsignment } from './Consignment';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../../../services/admin/consignment.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { typeCommissionList } from '../../../../libraries/type.commission.library';

import AppLegend from '../../../../components/element/Legend';
import AppInputContact from '../../../../components/element/InputContact';
import AppPreloader from '../../../../components/Preloader';

export interface ConsignmentCreatePageProps {};

let errorResponse: Error;

const ConsignmentCreatePage: React.FunctionComponent<ConsignmentCreatePageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser, contactsForCompany, loadContactsForCompany} = UseOutletContextConsignment()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [provider, setProvider] = useState({value: '', valid: false, object: null as any})
  const [commissionType, setCommissionType] = useState({value: 'percentage', valid: true})
  const [commissionRate, setCommissionRate] = useState({value: '', valid: false})
  const [startDate, setStartDate] = useState({value: '', valid: false})
  const [endDate, setEndDate] = useState({value: '', valid: true})
  const [notes, setNotes] = useState('')

  const handleChangeCommissionType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCommissionType({value: event.target.value, valid: true})
    if (event.target.value === 'none') {
      setCommissionRate({value: '0', valid: true})
    } else {
      setCommissionRate({value: '', valid: false})
    }
  }

  const handleChangeCommissionRate = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value
    if (value.length > 0 && parseFloat(value) >= 0) {
      setCommissionRate({value: value, valid: true})
      legendValid('legend-commission-rate-required')
    } else {
      setCommissionRate({value: value, valid: false})
      legendInvalid('legend-commission-rate-required')
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setStartDate({value: event.target.value, valid: true})
      legendValid('legend-start-date-required')
    } else {
      setStartDate({value: event.target.value, valid: false})
      legendInvalid('legend-start-date-required')
    }
  }

  const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate({value: event.target.value, valid: true})
  }

  const handleChangeNotes = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value)
  }

  const submitConsignmentCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && provider.valid && commissionType.valid && commissionRate.valid && startDate.valid) {
      await ApiDigitalAdminConsignment.postConsignment(
        companyForUser.id,
        provider.object,
        commissionType.value,
        parseFloat(commissionRate.value),
        startDate.value,
        endDate.value,
        notes
      ).then( (response) => {
        if (response.status === 201) {
          GlobalSwal.fire({
            title: lang.labels.successfullyCreated,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/purchase/consignment/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!provider.valid) legendInvalid('legend-contact-required')
        if (!commissionRate.valid) legendInvalid('legend-commission-rate-required')
        if (!startDate.valid) legendInvalid('legend-start-date-required')
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.consignments, branch: lang.labels.newConsignment}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contactsForCompany
        ?
        <div className="form">
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h2>{lang.labels.newConsignment}</h2>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row mb-3">
                <label className="form-label required">{lang.labels.provider}</label>
                <div className="fv-row">
                  <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} contact={provider} setContact={setProvider} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
                </div>
              </div>
              <div className="row mb-3">
                <label className="form-label required">{lang.labels.commissionType}</label>
                <div className="fv-row">
                  <select className="form-select" name="commission_type" value={commissionType.value} onChange={handleChangeCommissionType}>
                    { typeCommissionList(lang).map((item, index) => (
                      <option key={index} value={item.code}>{item.display}</option>
                    ))}
                  </select>
                </div>
              </div>
              { commissionType.value !== 'none' &&
                <div className="row mb-3">
                  <label className="form-label required">{lang.labels.commissionRate}{commissionType.value === 'percentage' ? ' (%)' : ''}</label>
                  <div className="fv-row">
                    <input className="form-control" type="number" name="commission_rate" min="0" step="0.01" value={commissionRate.value} onChange={handleChangeCommissionRate} />
                    <AppLegend attribute={{check: commissionRate.valid, name: "commission-rate", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
              }
              <div className="row mb-3">
                <label className="form-label required">{lang.labels.startDate}</label>
                <div className="fv-row">
                  <input className="form-control" type="date" name="start_date" value={startDate.value} onChange={handleChangeStartDate} />
                  <AppLegend attribute={{check: startDate.valid, name: "start-date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              <div className="row mb-3">
                <label className="form-label">{lang.labels.endDate}</label>
                <div className="fv-row">
                  <input className="form-control" type="date" name="end_date" value={endDate.value} onChange={handleChangeEndDate} />
                </div>
              </div>
              <div className="row mb-0">
                <label className="form-label">{lang.labels.notes}</label>
                <div className="fv-row">
                  <TextareaAutosize className="form-control" name="notes" minRows={2} value={notes} onChange={handleChangeNotes} />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
            <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
              <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                  <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                </svg>
              </span>
              <div className="d-grid">
                <span className="text-start text-dark fw-bolder fs-5">{lang.labels.newConsignment}</span>
                <span className="text-justify text-muted fw-bold">
                  {lang.labels.clickOn}
                  <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.create}</span>
                  {lang.labels.toContinue}
                </span>
              </div>
            </div>
            <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitConsignmentCreate}>
              <span className="indicator-label text-uppercase text-nowrap">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ConsignmentCreatePage;
